import { ReactComponent as SuccessIcon } from '../assets/checkmark.svg';
import hatchbackDogImgPng from '../assets/Hatchback_dog5.png';
import hatchbackDogImgWebP from '../assets/Hatchback_dog5.webp';
import builtOnSureLogo from '../assets/toggle/image/built-on-sure-logo.svg';
import errorImg from '../assets/toggle/image/errorImg.png';
import tenantLoveImg from '../assets/toggle/image/protect-loved-ones.svg';
import tenantAssetsImg from '../assets/toggle/image/protect-your-assets.svg';
import tenantVehicleImg from '../assets/toggle/image/protect-your-vehicle.svg';
import { ReactComponent as InverseToggleLogo } from '../assets/toggle/image/TAFC-inverse.svg';
import { ReactComponent as LightToggleLogo } from '../assets/toggle/image/TAFC-ongreen_1.svg';
import { ReactComponent as TenantCarIcon } from '../assets/toggle/image/toggle-car-front.svg';
import tenantCityImg from '../assets/toggle/image/toggle-little-city-lilo.png';
import ufoImg from '../assets/ufo.png';
import KnockoutCopy from '../components/KnockoutCopy/KnockoutCopy';
import { TWlConfig } from '../interfaces';
import { isServer } from '../services/env';
import mergeConfig from '../services/mergeConfig';
import { prepUrlForEnvironment } from '../utils';

const baseUrl = prepUrlForEnvironment({
  url: process.env.REACT_APP_ACCOUNT_BASE_URL,
});
const mfaSuccessHost = prepUrlForEnvironment({
  url: process.env.REACT_APP_MFA_SUCCESS_REDIRECT_HOST,
});

const defaultConfig: TWlConfig = {
  addonModalConfig: {
    carbonOffsetDescriptionCopy:
      'For Toggle auto insurance policies, at no extra cost, we purchase carbon offsets in greenhouse gas reducing projects equal to the miles driven in a policy term. Miles driven is calculated using a national average as determined by the U.S. Department of Transportation (DOT).',
  },
  buckleUpConfig: {
    defaultHeader: 'Buckle up!',
  },
  cmsSlugs: {
    authenticatedFooterSlug: 'authenticated-footer',
    copyrightSlug: 'copyright',
    footerSlug: 'global-footer',
    privacyPolicySlug: 'privacy-policy',
    termsViewSlug: 'terms-and-conditions',
  },
  coverageConfig: {
    header: `Let's customize your coverage.`,
    subHeader: `To start, we've made selections based on your current policy. Adjust things to get the exact coverage you want. Or just skip to the next step.`,
    tenantAssetsImg,
    tenantLoveImg,
    tenantVehicleImg,
    vehicleCoverageHeaderCopy: {
      description:
        'How important is your car to you? How much do you depend on it? If something happens to it, these coverages will help get you back on the road.',
      image: 'vehicle',
      title: 'Protect your vehicles',
    },
  },
  coverageStartDateConfig: {
    infoCopy: null,
    labelCopy: 'Select a start date',
  },
  discountsConfig: {
    hasCarbonOffsetDiscount: true,
    hasPartnerDiscount: false,
  },
  errorPageConfig: {
    backgroundImgSrc: errorImg,
  },
  featureFlags: {
    isAccountManagementEnabled: true,
    isAutoPurchaseFlowEnabled: true,
    isAutoQuoteLinkExchangeEnabled: false,
    isDirectToAutoQuoteEnabled: true,
    isLogoRedirectInAutoPurchaseFlowEnabled: false,
    isPrivacyAndTermsEnabled: true,
    isRentersQuoteCreationEnabled: true,
    isSelfServiceEnabled: true,
  },
  footerConfig: {
    hasNavItems: true,
    hasSocialLinks: true,
    sureFooterLogoSrc: builtOnSureLogo,
    tenantPhoneVariant: 'first-row',
  },
  iconConfig: {
    isShowAlertIcon: true,
    isShowDriverIcon: true,
    isShowPersonIcon: true,
    isShowVehicleIcon: true,
  },
  icons: {
    SuccessIcon,
    TenantCarIcon,
    trustIcons: [
      'pf-visa',
      'pf-mastercard-alt',
      'pf-american-express-alt',
      'pf-discover',
      'pf-jcb',
      'pf-diners',
    ],
  },
  iidKnockoutCopy: {
    featureFlag: 'isUpdatedToggleKnockoutCopy',
    header: `We're sorry but we are unable to provide you with a quote at this time.`,
    subHeader: <KnockoutCopy phoneNumber="855-864-1530" />,
  },
  layoutContentConfig: {
    tenantCityImg,
  },
  layoutQuoteConfig: {
    isLogoHeader: false,
    summaryPosition: 'fixed',
    variant: '100vh-quote-summary',
  },
  localforageDbName: 'toggle',
  logos: {
    default: InverseToggleLogo,
    light: LightToggleLogo,
    title: 'Toggle Logo',
    toggleDefault: null,
    toggleLight: null,
  },
  modalAdjustedPriceConfig: {
    footerButtonVariant: 'SECONDARY',
  },
  navBarConfig: {
    hasNavBarItems: true,
  },
  noAutoCoverageConfig: {
    ctaSubmitCopy: `Thanks! We'll be in touch as soon as Toggle is available in your state.`,
    getNoCoverageCopy: ({ regionName, isEligibleForRenters }) => {
      return isEligibleForRenters
        ? `We'd love to toggle with you, but we don't yet have auto insurance available in ${regionName}.`
        : `We'd love to toggle with you, but we're not in ${regionName} yet.`;
    },
    secondaryCopy: 'Let me know when Toggle comes my way',
  },
  notFoundPageConfig: {
    backgroundImgSrc: ufoImg,
    ctaHref: '/',
  },
  passwordValidator: {
    passingIconType: 'outline',
  },
  primaryVehicleDataSource: undefined,
  purchaseFlowConfig: {
    checkoutExistingToggleCustomerAlreadyLoggedInCopy:
      "Looks like you're already a Toggle customer. Nice!",
    checkoutExistingToggleCustomerLoginSuccessCopy: 'Welcome back, Toggler!',
    checkoutExistingToggleCustomerPrompt: 'Are you already a Toggle customer?',
    checkoutToggleCustomerDefault: undefined,
  },
  quoteExpirationCondition: {
    maxElapsedTimeMinutes: 60,
    onConfirmDestination: '/auto/enroll/start',
    timestampKey: 'updated_at',
  },
  redirectUrls: {
    indexRedirectUrl: '',
    logoRedirectUrl: `${baseUrl}`,
    mfaSuccessRedirectUrl: `${mfaSuccessHost}my-account`,
    navBlockerRedirectUrl: `${baseUrl}auto/enroll/start`,
    isPartnerRedirectUrlEnabled: false,
  },
  signUpViewConfig: {
    backgroundPicture: {
      alt: 'A man and his dog',
      sources: [
        {
          src: hatchbackDogImgWebP,
          type: 'image/webp',
        },
        {
          src: hatchbackDogImgPng,
          type: 'image/png',
        },
      ],
      fallbackSrc: hatchbackDogImgPng,
    },
    title: 'Cover Your Stuff | Toggle Insurance',
    header:
      "Get auto insurance and peace of mind in minutes. Customize it so you don't have to pay for coverage you don't want.",
    hero1: 'Get in, ',
    hero2: "Let's go.",
    heroVariant: 'large',
    informationalHeader: 'But first...',
    informationalSubHeader: 'Tell us a little about yourself.',
  },
  stripeElStyles: {
    base: {
      '::placeholder': {
        color: '#9b9b9b',
      },
      color: '#000',
      fontFamily: "'GTHaptik', Helvetica, sans-serif",
      fontSize: '16px',
    },
    fonts: [
      {
        family: 'GTHaptik',
        // NOTE: this using relative URLs here causes issues with localhost (need https for non-mixed content), and e2ws, sandbox, and QA since they're password protected
        src: `url(https://sure-host.s3.amazonaws.com/static/farmers/GT-Haptik-Regular-2.ttf) format("truetype")`,
      },
    ],
    invalid: {
      color: '#000',
      iconColor: '#df436e',
    },
  },
  tenantDisplayName: 'Toggle',
  tenantPhoneNumber: '1-855-864-1530',
  vehicleManagementCopyConfig: {
    subheader:
      "We've added the vehicles from your current policy. Feel free to add more or edit our suggestions.",
  },
  welcomeModalConfig: {
    variant: {
      toyota: {
        bodyCopy:
          'We teamed up with Toggle, a member of the Farmers® Insurance family, to bring Toyota Auto Insurance to life. You might notice a different look, but you will continue to experience the same exceptional service that you’ve come to expect from Toyota.',
        headerCopy: "You're now heading to Toggle®...",
        nextButtonCopy: 'OK',
      },
    },
  },
};

function retrieveConfig(filename = '') {
  try {
    // eslint-disable-next-line import/no-dynamic-require, global-require
    return require(`./whitelabel/${filename}`);
  } catch (e) {
    return null;
  }
}

function getEnvConfig(overrideFileName = '') {
  if (isServer || !process.env.REACT_APP_WL_CONFIG) {
    return {};
  }

  const { wlConfigTestHelper, Cypress } = window;

  const configFileName = process.env.REACT_APP_WL_CONFIG;
  const config =
    (overrideFileName && retrieveConfig(overrideFileName)) ||
    retrieveConfig(configFileName);

  return (
    (Cypress && wlConfigTestHelper && wlConfigTestHelper(config)) ||
    config ||
    {}
  );
}

export function configWithDefaults(overrideFileName = ''): TWlConfig {
  return mergeConfig<TWlConfig>(
    defaultConfig,
    getEnvConfig(overrideFileName).default
  );
}

export default configWithDefaults();
